import { TGetValueFn } from "@components/smart/FieldInfo";
import { addNewLineItem } from "@components/smart/smartFastEntryList";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    EntityTypeName,
    IPrMaternityLeaveEntitlementEntity,
    PrCareReceiverChildEntity,
    PrCSSZSubmissionDetailEntity,
    PrCSSZSubmissionMaternityLeaveDetailEntity,
    PrCSSZSubmissionMaternityLeaveEntity,
    PrMaternityLeaveEntitlementEntity
} from "@odata/GeneratedEntityTypes";
import { PrMaternityLeaveEntitlementCode, PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { IdentificationTypeLocalPath } from "@pages/payroll/employee/Employee.utils";
import { maternityLeaveEntitlementTransformation } from "@pages/payroll/socialEvents/eventTypes/MaternityLeaveDef";
import { addCareReceiverDefinitions } from "@pages/payroll/socialEvents/SocialEventsDef";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import {
    addBasicBenefitRequestGroup,
    addCommonNEMPRIDefinitions,
    addMaternityRelatedFieldDefinitions,
    addPensionFieldsDefinitions,
    CSSZTranslationFiles,
    distrainmentAndInsolvencyFields,
    PrCSSZSubmissionEntity
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import PrCSSZSubmissionMaternityLeaveFormView
    from "@pages/payroll/submission/submissionTypes/PrCSSZSubmissionMaternityLeaveFormView";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { BasicInputSizes, FastEntryInputSizes, FieldType } from "../../../../enums";
import { addFormDef, addFormGroup, addRowsToGroup } from "../../../../views/formView/Form.utils";
import { FormStorage } from "../../../../views/formView/FormStorage";

const AddCareReceiverPath = BindingContext.localContext("addCareReceiverPath");

export function addCareReceiverEmptyRow(storage: FormStorage, childrenBc: BindingContext): void {
    const columns = [
        { id: IdentificationTypeLocalPath }
    ];
    addNewLineItem(storage, childrenBc.getNavigationPath(), PrSubmissionGroup.CareReceiver, columns);
}

const _detailPath = (path: PrCSSZSubmissionMaternityLeaveDetailEntity, ...restProps: string[]) =>
    createPath(PrCSSZSubmissionMaternityLeaveEntity.Detail, path, ...restProps);

const isOtherReasonSelectedThanGivingBirth: TGetValueFn<boolean> = ({ storage }) => {
    const reason = storage.getValueByPath(_detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.MaternityReason)) as IPrMaternityLeaveEntitlementEntity;
    return !!reason && reason?.Code !== PrMaternityLeaveEntitlementCode.GivingBirth;
};

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrCSSZSubmissionMaternityLeaveFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication,
        subType: PrSubmissionSubtypeCode.NempriMaternityLeave
    }));

    // shared definitions with paternity leave
    addCommonNEMPRIDefinitions(d, context, args);

    // Maternity and Paternity leave has conditionally required decision number,
    // but we don't want to define the condition in the form logic -> let it on the user
    d.form.fieldDefinition[createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionDetailEntity.DecisionNumber)].isRequired = false;

    // **************** Pension, Previous maternity, Insolvency, Distrainment, ... ****************
    const pensionRows = addPensionFieldsDefinitions(d, PrCSSZSubmissionMaternityLeaveDetailEntity);
    const maternityRelatedRows = addMaternityRelatedFieldDefinitions(d, PrCSSZSubmissionMaternityLeaveDetailEntity, { enhanced: true });
    const distrainmentAndInsolvencyRows = distrainmentAndInsolvencyFields(d, PrCSSZSubmissionMaternityLeaveDetailEntity);

    const rows = [
        ...pensionRows,
        ...maternityRelatedRows,
        ...distrainmentAndInsolvencyRows
    ];
    addRowsToGroup(d, PrSubmissionGroup.IncomeInPeriod, rows);

    // **************** BenefitRequest ****************
    addFormDef(d, _detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.DateStart), {
        label: i18next.t("Submission:SocialEventsForm.DateStart"),
    });
    addFormDef(d, _detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.MaternityReason), {
        type: FieldType.ComboBox,
        label: i18next.t("Submission:SocialEventsForm.ReasonMaternity"),
        width: BasicInputSizes.XXL,
        isRequired: true,
        defaultValue: PrMaternityLeaveEntitlementCode.GivingBirth,
        fieldSettings: {
            displayName: PrMaternityLeaveEntitlementEntity.Name,
            // noRecordText: i18next.t("Common:Select.NoRecord"),
            transformFetchedItems: maternityLeaveEntitlementTransformation
        },
        affectedFields: [{ id: AddCareReceiverPath }]
    });
    addBasicBenefitRequestGroup(d, [
        [{ id: _detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.DateStart) }],
        [{ id: _detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.MaternityReason) }]
    ]);

    // **************** CareReceiver definition ****************
    const careReceiverRows = addCareReceiverDefinitions(d, _detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.Children), false);
    // ungroup Name and Surname columns in collection
    [PrCareReceiverChildEntity.FirstName, PrCareReceiverChildEntity.LastName].forEach(prop => {
        delete d.form.fieldDefinition[createPath(_detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.Children), prop)].groupedField;
    });
    // shorten identification value columns
    [PrCareReceiverChildEntity.IdentificationNumber, PrCareReceiverChildEntity.DateOfBirth].forEach(prop => {
        d.form.fieldDefinition[createPath(_detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.Children), prop)].width = FastEntryInputSizes.S;
    });
    addFormGroup(d, {
        id: PrSubmissionGroup.CareReceiver,
        showGroupDivider: false,
        title: i18next.t("Submission:SocialEventsForm.Children"),
        lineItems: {
            collection: _detailPath(PrCSSZSubmissionMaternityLeaveDetailEntity.Children),
            disableVirtualization: true,
            canAdd: true,
            canReorder: false,
            isItemCloneable: false,
            isTheOnlyItemRemovable: false,
            columns: careReceiverRows?.flat(),
            order: PrCareReceiverChildEntity.Order
        },
        isVisible: isOtherReasonSelectedThanGivingBirth,
    }, PrSubmissionGroup.BenefitRequest);

    return {
        ...d,
        entitySet: EntitySetName.PrCSSZSubmissionsMaternityLeave
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
    ...CSSZTranslationFiles,
];
setDefByEntityType(EntityTypeName.PrCSSZSubmissionMaternityLeave, getDefinitions);